import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Toolbar,
  Typography,
  Button,
  Checkbox,
  Divider,
  ListItemText,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  IconButton,
} from '@mui/material';
 
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { loadModules } from 'esri-loader';
import { GetPropertyTypes, GetPropertiesForGrid } from '../../REST/properties';

import { stringCompare, numberCompare, dateCompare } from '../../helpers/dates';
import Access from '../../Utils/authorize';
import Applicants from './applicants';
import {GetMapLayers, AddUpdateMapLayer} from '../../REST/maplayer';
import { createCombinedControlForEsriLoader } from '../../Components/Gis';
import '../../Components/Gis/LayerManagerWidget.css';
import '../../Components/Gis/LayerConfigBuilderWidget.css';
 

const sortFields = [
  { name: 'Rent', value: 'rent' },
  { name: 'Name', value: 'name' },
  { name: 'Availability', value: 'recentAvailability' },
  { name: 'Property Type', value: 'prpType' }
];

const statusOptions = [
  { key: 'agreementExpired', label: 'Agreement Expired', color: 'red' },
  { key: 'notpaid', label: 'Invoice Not Paid', color: 'orange' },
  { key: 'insuranceExpired', label: 'Insurance Expired', color: 'blue' },
  { key: 'allGood', label: 'All Good', color: 'green' },
];

const PROPERTY_PLACEHOLDER_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 220">
      <rect width="320" height="220" rx="18" fill="#f3f4f6"/>
      <rect x="18" y="18" width="284" height="184" rx="14" fill="#e5e7eb"/>
      <path d="M78 144l42-42 34 34 52-58 36 36v34H78z" fill="#cbd5e1"/>
      <circle cx="114" cy="84" r="16" fill="#dbeafe"/>
      <rect x="94" y="164" width="132" height="12" rx="6" fill="#cbd5e1"/>
      <rect x="122" y="182" width="76" height="10" rx="5" fill="#d1d5db"/>
    </svg>
  `);

const MapComponent = ({ internal, data, selectedRow, statusFilters, toggleStatusFilter }) => {
  const mapRef = useRef(null);
  const [view, setView] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenBtnRef = useRef(null);

  const toggleFullscreen = () => {
    setIsFullscreen(prev => {
      const newState = !prev;
      // Update button icon
      if (fullscreenBtnRef.current) {
        if (newState) {
          fullscreenBtnRef.current.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" fill="currentColor"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>`;
        } else {
          fullscreenBtnRef.current.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>`;
        }
      }
      return newState;
    });
  };

useEffect(() => {
  loadModules(
    ['esri/Map', 'esri/views/MapView', 'esri/widgets/Search', 'esri/widgets/BasemapToggle'],
    { css: true }
  )
    .then(async ([EsriMap, MapView, Search, BasemapToggle]) => {
      const map = new EsriMap({ basemap: 'streets-navigation-vector' });

      const mv = new MapView({
        container: mapRef.current,
        map,
        center: [-97.02855, 34.29987],
        zoom: 10,
      });
      
      // Create fullscreen toggle button
      const fullscreenBtn = document.createElement('div');
      fullscreenBtn.className = 'esri-widget esri-widget--button esri-interactive';
      fullscreenBtn.style.cssText = 'display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; cursor: pointer;';
      fullscreenBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>`;
      fullscreenBtn.title = 'Toggle Fullscreen';
      fullscreenBtnRef.current = fullscreenBtn;
      
      fullscreenBtn.addEventListener('click', () => {
        toggleFullscreen();
      });
      
      mv.ui.add(fullscreenBtn, 'bottom-right');
      mv.ui.add(new Search({ view: mv }), 'bottom-right');
      mv.ui.add(new BasemapToggle({ view: mv, nextBasemap: 'satellite' }), 'bottom-left');
     
      mv.ui.remove('zoom');
      await createCombinedControlForEsriLoader({
        view: mv,
        canEditConfig:  Access.isAdmin('Settings'),
        collapsedByDefault: false,
        loadConfig: async () => {
          const raw = await GetMapLayers(); // or localStorage/db
          return raw ? raw.data : [];
        },
        saveConfig: async (configs) => {
          const result = await AddUpdateMapLayer(configs); // async persistence
          return result.data;
        },
        defaultLayerConfigs: [],
      });

      setView(mv);
    })
    .catch(console.error);
}, []);


  useEffect(() => {
    if (!view) return;
    view.graphics.removeAll();
    loadModules([
      'esri/geometry/Point', 'esri/Graphic',
      'esri/symbols/SimpleMarkerSymbol', 'esri/PopupTemplate'
    ]).then(([Point, Graphic, SimpleMarkerSymbol, PopupTemplate]) => {
      const colorMap = statusOptions.reduce((acc, opt) => { acc[opt.key] = opt.color; return acc; }, {});
      const getCategory = e => e.agreementExpired ? 'agreementExpired' : e.notpaid ? 'notpaid' : e.insuranceExpired ? 'insuranceExpired' : 'allGood';
      const pts = [];
      data.forEach(entry => {
        const cat = getCategory(entry);
        const pt = new Point({ latitude: entry.location.coordinates[0], longitude: entry.location.coordinates[1] });
        const imageUrl = entry.headerImage 
          ? `${config.REACT_APP_API_URL}/api/Property/List/Images/${entry.headerImage}` 
          : 'https://via.placeholder.com/200x100?text=No+Image';
        const popup = new PopupTemplate({
          title: entry.propertyName || 'Details',
          content: `
            <div style="text-align:center;"><img src="${imageUrl}" style="width:200px;height:auto;margin-bottom:8px;"/></div>
            <table style="width:100%;font-size:14px;">
              <tr><td><strong>Property:</strong></td><td>${entry.propertyName}</td></tr>
              <tr><td><strong>Area:</strong></td><td>${entry.areaName}</td></tr>
              <tr><td><strong>Agreement Expired:</strong></td><td>${entry.agreementExpired ? 'Yes' : 'No'}</td></tr>
              <tr><td><strong>Invoice Not Paid:</strong></td><td>${entry.notpaid ? 'Yes' : 'No'}</td></tr>
              <tr><td><strong>Insurance Expired:</strong></td><td>${entry.insuranceExpired ? 'Yes' : 'No'}</td></tr>
            </table>
          `
        });
        const sym = new SimpleMarkerSymbol({ size: 8, color: colorMap[cat], outline: { color: 'white', width: 1 } });
        view.graphics.add(new Graphic({ geometry: pt, symbol: sym, popupTemplate: popup }));
        pts.push(pt);
      });
      if (pts.length) view.goTo(pts).catch(console.error);
      if (selectedRow) {
        const sel = data.find(d => d._id === selectedRow._id);
        if (sel) view.goTo({ target: new Point({ latitude: sel.location.coordinates[0], longitude: sel.location.coordinates[1] }), zoom: 14 }).catch(console.error);
      }
    }).catch(console.error);
  }, [view, data, selectedRow]);

  return (
    <Box sx={{ 
      position: isFullscreen ? 'fixed' : 'relative', 
      width: isFullscreen ? '100vw' : '100%', 
      height: isFullscreen ? '100vh' : '100%',
      top: isFullscreen ? 0 : 'auto',
      left: isFullscreen ? 0 : 'auto',
      zIndex: isFullscreen ? 9999 : 'auto',
      backgroundColor: isFullscreen ? '#fff' : 'transparent'
    }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

      {internal &&
        (
          <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(255,255,255,0.9)', p: 1, borderRadius: 1, boxShadow: 1, maxWidth: 180 }}>
            <Typography variant="subtitle2">Property Status</Typography>
            <Divider sx={{ my: 1 }} />
            {statusOptions.map(opt => (
              <Box key={opt.key} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, cursor: 'pointer' }} onClick={() => toggleStatusFilter(opt.key)}>
                <Box sx={{ width: 12, height: 12, bgcolor: opt.color, border: '1px solid #444', mr: 1, opacity: statusFilters[opt.key] ? 1 : 0.3 }} />
                <Typography variant="caption" sx={{ opacity: statusFilters[opt.key] ? 1 : 0.3 }}>{opt.label}</Typography>
              </Box>
            ))}
          </Box>
        )
      }
    </Box>
  );
};

export default function LeaseListing({ internal = false, edit = () => { } }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [data, setData] = useState([]);
  const [prpTypes, setPrpTypes] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pageSize, setPageSize] = useState(10);

  // Filter states
  const [searchText, setSearchText] = useState('');

  const [statusFilters, setStatusFilters] = useState(statusOptions.reduce((acc, opt) => { acc[opt.key] = true; return acc; }, {}));

  const handleStatusSelect = e => {
    const selectedKeys = e.target.value;
    setStatusFilters(statusOptions.reduce((acc, opt) => { acc[opt.key] = selectedKeys.includes(opt.key); return acc; }, {}));
  };

  const handleSortOrder = (_, value) => {
    if (value) {
      setSortOrder(value);
    }
  };

  useEffect(() => {
    (async () => {
      const types = await GetPropertyTypes()
      const values = await GetPropertiesForGrid()
      setPrpTypes(types.data)
      const data = values.data.map(each => {
        const typeofProp = types.data.find(e => e._id === each.propertyType)
        return { ...each, recentAvailability: each.dateAvailable, prpType: typeofProp ? typeofProp.name : '' }
      })
      setData(data)

    })()
  }, [])

  const compare = (a, b, field, filterDir) => {
    let ret = 0
    if (a[field] && b[field]) {
      if (field === 'rent') ret = numberCompare(a[field], b[field])
      if (field === 'recentAvailability') ret = dateCompare(a[field], b[field])
      if (field === 'name') ret = stringCompare(a[field], b[field])
      if (field === 'prpType') ret = stringCompare(a[field], b[field])
      if (filterDir == "asc") {

        return ret ? 1 : -1
      }
      else {

        return ret ? -1 : 1
      }
    }

    return ret
  }
  const rows = useMemo(() => {
    let filtered = [...data];
    
    // Property Type filter
    if (filterCategory) filtered = filtered.filter(r => r.propertyType === filterCategory);
    
    // Search text filter (name, address, OR rent)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(r => 
        (r.name && r.name.toLowerCase().includes(searchLower)) ||
        (r.propertyName && r.propertyName.toLowerCase().includes(searchLower)) ||
        (r.address && r.address.toLowerCase().includes(searchLower)) ||
        (r.rent && r.rent.toString().includes(searchText))
      );
    }
    
    // Sort
    filtered.sort((a, b) => compare(a, b, sortField, sortOrder));
    
    // Agreement/Invoice/Insurance status filters
    return filtered.filter(entry => {
      const cat = entry.agreementExpired ? 'agreementExpired' : entry.notpaid ? 'notpaid' : entry.insuranceExpired ? 'insuranceExpired' : 'allGood';
      return statusFilters[cat];
    });
  }, [data, filterCategory, searchText, sortField, sortOrder, statusFilters]);

  const handleRowSelection = sel => setSelectedRow(rows.find(r => r._id === sel[0]) || null);
  const columns = [
    {
      field: 'custom',
      disableColumnFilter: true, sortable: false,
      headerName: 'Properties',
      flex: 1,
      renderCell: (params) => {
        const { headerImage, name, description, rent, propertyName, areaName, recentAvailability, _id, status, agreementId, prpType } = params.row
        
        // Only show View Lease button when status is 'Leased' and agreementId exists
        const isLeased = status === 'Leased' && agreementId;

        if (isMobile) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                p: 1.5,
                gap: 1.5,
                justifyContent: 'flex-start',
              }}
            >
              <Box sx={{ width: '100%', height: 180, flexShrink: 0 ,display: 'flex'}}>
                <img
                  src={headerImage ? config.REACT_APP_API_URL + '/api/Property/List/Images/' + headerImage : PROPERTY_PLACEHOLDER_IMAGE}
                  alt={headerImage ? propertyName : "No Image"}
                  onError={(evt) => {
                    evt.currentTarget.onerror = null;
                    evt.currentTarget.src = PROPERTY_PLACEHOLDER_IMAGE;
                  }}
                  style={{ width: "100%", height: "100%", objectFit: 'cover', borderRadius: 8 }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {prpType}{rent ? ` • $${rent} / month` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {areaName}{recentAvailability ? ` • Available ${new Date(recentAvailability).toLocaleDateString()}` : ''}
                </Typography>
                {description && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {description}
                  </Typography>
                )}
              </Box>

              {internal && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    pt: 0.5,
                  }}
                >
                  {edit && (
                    <Button
                      onClick={() => edit(_id)}
                      size='small'
                      variant='contained'
                      startIcon={<EditIcon />}
                      fullWidth
                    >
                      Edit
                    </Button>
                  )}
                  {isLeased && (
                    <Button
                      onClick={() => navigate('/agreements/' + agreementId)}
                      size='small'
                      variant='outlined'
                      startIcon={<VisibilityIcon />}
                      color="primary"
                      fullWidth
                    >
                      View Lease
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          )
        }
        
        
        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '180px minmax(0, 1fr) 150px' },
              alignItems: 'stretch',
              columnGap: 2,
              rowGap: 1.5,
              width: '100%',
              p: 1.5,
            }}
          >
            <Box sx={{ width: { xs: '100%', md: 180 }, height: { xs: 180, md: 180 }, justifySelf: { xs: 'stretch', md: 'start' } }}>
              <img
                src={headerImage ? config.REACT_APP_API_URL + '/api/Property/List/Images/' + headerImage : PROPERTY_PLACEHOLDER_IMAGE}
                alt={headerImage ? propertyName : "No Image"}
                onError={(evt) => {
                  evt.currentTarget.onerror = null;
                  evt.currentTarget.src = PROPERTY_PLACEHOLDER_IMAGE;
                }}
                style={{ width: "100%", height: "100%", objectFit: 'cover', borderRadius: 8 }}
              />
            </Box>

            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                {name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {prpType}{rent ? ` • $${rent} / month` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {areaName}{recentAvailability ? ` • Available ${new Date(recentAvailability).toLocaleDateString()}` : ''}
              </Typography>
              {description && (
                <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: '72ch' }}>
                  {description}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                justifyContent: { xs: 'flex-start', md: 'center' },
                alignItems: { xs: 'stretch', md: 'flex-end' },
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                gap: 1,
                minWidth: 0,
              }}
            >
              {internal && (
                <>
                  {edit && (
                    <Button
                      onClick={() => edit(_id)}
                      size='small'
                      variant='contained'
                      startIcon={<EditIcon />}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      Edit
                    </Button>
                  )}
                  {isLeased && (
                    <Button
                      onClick={() => navigate('/agreements/' + agreementId)}
                      size='small'
                      variant='outlined'
                      startIcon={<VisibilityIcon />}
                      color="primary"
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      View Lease
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        )
      }

    }]

  return (
    <Box
      display="flex"
      width="100%"
      height={{ xs: 'auto', md: '100%' }}
      flexDirection={{ xs: 'column', md: 'row' }}
    >
      {!isMobile && (
        <Box width="50%" height="100%" p={2}>
          <MapComponent
            internal={internal} data={rows} selectedRow={selectedRow}
            statusFilters={statusFilters}
            toggleStatusFilter={k => e => setStatusFilters(f => ({ ...f, [k]: !f[k] }))} />
        </Box>
      )}
      <Box width={{ xs: '100%', md: '50%' }} height={{ xs: 'auto', md: '100%' }} display="flex" flexDirection="column">
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1.5,
            px: { xs: 1, md: 2 },
            py: 2,
            minHeight: 'auto !important',
            backgroundColor: 'background.paper',
          }}
        >
          {/* Search and Sort Row */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              variant="outlined"
              sx={{ 
                flex: 1,
                minWidth: { xs: 'calc(100% - 140px)', md: 150 }
              }}
              InputProps={{
                endAdornment: searchText && (
                  <IconButton size="small" onClick={() => setSearchText('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140, display: { xs: 'none', md: 'inline-flex' } }}>
              <InputLabel>Property Type</InputLabel>
              <Select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} label="Property Type">
                <MenuItem value=""><em>All</em></MenuItem>
                {prpTypes.map(({ name, _id }) => <MenuItem key={_id} value={_id}>{name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120, display: { xs: 'none', md: 'inline-flex' } }}>
              <InputLabel id="sort-field-label">Sort by</InputLabel>
              <Select labelId="sort-field-label" value={sortField} onChange={e => setSortField(e.target.value)} label="Sort by">
                {sortFields.map(f => <MenuItem key={f.value} value={f.value}>{f.name}</MenuItem>)}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              exclusive
              value={sortOrder}
              onChange={handleSortOrder}
              size="small"
            >
              <ToggleButton value="asc" sx={{ minWidth: 60 }}>Asc</ToggleButton>
              <ToggleButton value="desc" sx={{ minWidth: 60 }}>Desc</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Toolbar>
        <Box sx={{ flex: '1 1 auto', minHeight: { xs: 640, md: 0 } }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowsPerPageOptions={[10, 25, 50]}
            getRowId={r => r._id}
            rowHeight={isMobile ? 500 : 210}
            sx={{
              width: '100%',
              height: '100%',
              '& .MuiDataGrid-cell': {
                alignItems: 'flex-start',
              },
              '& .MuiDataGrid-cellContent': {
                width: '100%',
              },
            }}
            onSelectionModelChange={handleRowSelection}
          />
        </Box>
      </Box>
      {!internal && !Access.user() && isMobile && <Applicants floating />}
    </Box>
  );
}
